"use client";

import PageTitle from "@/components/PageTitle";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type MouseEvent } from "react";
import { flushSync } from "react-dom";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Tape } from "@/components/Craft";
import GalleryModal from "@/components/GalleryModal";
import {
  filterLabel,
  filterPath,
  filterSwatch,
  filterHeadline,
  filterTitle,
  filters,
  inFilter,
  intros,
  isFilter,
  projectBySlug,
  type Filter,
  type Project,
} from "@/lib/projects";
import { d, size } from "@/lib/site";

const SUFFIX = " · Polsterei ideensprung";

// A filter switch as a view transition (Chrome, Edge, Safari 18+): tiles in both lists glide to their new
// place (each has its own view-transition-name), the others fade, headline and text cross-fade. Elsewhere,
// or with reduced motion, it just switches.
function smoothly(update: () => void) {
  if (!document.startViewTransition || matchMedia("(prefers-reduced-motion: reduce)").matches) return update();
  document.startViewTransition(() => flushSync(update));
}

// long titles get a smaller marker on the tape, so it doesn't cover the photo (polsterei.css)
function tapeSize(title: string) {
  return title.length > 48 ? "ttag-xs" : title.length > 30 ? "ttag-s" : "";
}

// text next to the headline: a category's own intro (from ideensprung.de); Alle and Antik get the general one
function introText(f: Filter) {
  return f === "alle" || f === "antik"
    ? "Stöbern Sie in unseren Arbeiten – vom modernen Sofa über das liebevoll restaurierte Erbstück bis zum ausgefallenen Designermöbel. Vielleicht entdecken Sie dabei schon eine Idee für Ihr eigenes Lieblingsstück."
    : intros[f].p1;
}
const GAP_Y = 36;
// nominal column widths (px) per column count, used to pick the shortest column
const NOMINAL: Record<number, number> = { 1: 600, 2: 600, 3: 390, 4: 283 };

type Place = Record<string, string | number>;

// Masonry by shortest column for 1 to 4 columns. A tile's top is (sum of 1/ratio above) × column width
// + rows × gap, so it is written as CSS variables that polsterei.css turns into px against the grid width.
// Appending tiles never moves earlier ones; spacer[i] is the grid height with the first i+1 tiles.
function layout(list: Project[]) {
  const tiles: Place[] = list.map(() => ({}));
  const spacer: Place[] = list.map(() => ({}));
  for (const cols of [1, 2, 3, 4]) {
    const a = Array<number>(cols).fill(0);
    const n = Array<number>(cols).fill(0);
    const cw = NOMINAL[cols];
    list.forEach((p, i) => {
      let k = 0;
      for (let j = 1; j < cols; j++) if (a[j] * cw + n[j] * GAP_Y < a[k] * cw + n[k] * GAP_Y - 0.5) k = j;
      tiles[i][`--a${cols}`] = +a[k].toFixed(4);
      tiles[i][`--n${cols}`] = n[k];
      tiles[i][`--c${cols}`] = k;
      a[k] += 1 / p.ratio;
      n[k] += 1;
      const parts = a.map((ak, j) => (n[j] ? `calc(${ak.toFixed(4)} * var(--cw) + ${(n[j] - 1) * GAP_Y}px)` : "0px"));
      spacer[i][`--h${cols}`] = `max(${parts.join(", ")})`;
    });
  }
  return { tiles, spacer };
}

function subscribeSmall(cb: () => void) {
  const mq = matchMedia("(max-width: 1023px)");
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}

function pagesFromUrl() {
  const n = parseInt(new URLSearchParams(location.search).get("seite") ?? "1", 10);
  return n > 0 ? n : 1;
}

// where the address bar points inside /polsterei/: a filter, a project (popup), or something else
function readPath(): { filter?: Filter; project?: string } {
  const seg = location.pathname.replace(/^\/polsterei\/?/, "").replace(/\/$/, "");
  if (seg === "") return { filter: "alle" };
  if (isFilter(seg)) return { filter: seg };
  if (projectBySlug(seg)) return { project: seg };
  return {};
}

const plain = (e: MouseEvent) => e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

// Polsterei gallery: h1 and intro following the filter, filter swatches with counts (each its own route,
// switched client-side), masonry tiles with a calm before/after fade, paging, and the project popup.
// initialOpen: a project url (/polsterei/<slug>/) loaded directly shows the gallery with that popup open.
export default function Gallery({ initial, initialOpen }: { initial: Filter; initialOpen?: string }) {
  const [filter, setFilter] = useState<Filter>(initial);
  const [pages, setPages] = useState(1);
  const [open, setOpen] = useState<string | null>(initialOpen ?? null);
  // tiles whose before photo is mounted (loaded on first fade), and the one showing it right now
  const [primed, setPrimed] = useState<Set<string>>(() => new Set());
  const [fading, setFading] = useState<string | null>(null);
  const small = useSyncExternalStore(subscribeSmall, () => matchMedia("(max-width: 1023px)").matches, () => false);
  const per = small ? 12 : 18;

  const grid = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const back = useRef<string>(filterPath(initial));
  // true while the popup's history entry sits right after the gallery's, so closing can go back
  const pushed = useRef(false);

  const list = useMemo(() => inFilter(filter), [filter]);
  const places = useMemo(() => layout(list), [list]);
  const shown = Math.min(pages * per, list.length);
  const visible = list.slice(0, shown);

  // reveal on scroll: the next page loads when the end of the grid is within 600 px of the screen
  const sentinel = useRef<HTMLDivElement>(null);
  const more = shown < list.length;
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !more) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setPages((n) => n + 1), { rootMargin: "0px 0px 600px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [more, shown]);

  // ?seite=N on load; back/forward between filters and popups
  useEffect(() => {
    const first = requestAnimationFrame(() => setPages(pagesFromUrl()));
    const onPop = () => {
      const at = readPath();
      if (at.filter) {
        const f = at.filter;
        smoothly(() => {
          setFilter(f);
          setPages(pagesFromUrl());
          setOpen(null);
        });
        pushed.current = false;
      } else if (at.project) {
        setOpen(at.project);
        pushed.current = true;
      }
    };
    window.addEventListener("popstate", onPop);
    return () => {
      cancelAnimationFrame(first);
      window.removeEventListener("popstate", onPop);
    };
  }, []);

  // Before/after fade: one random tile in view at a time, about every 4–5 s. Not with reduced motion.
  useEffect(() => {
    if (open || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let timer = 0;
    let stop = false;
    let last = "";
    const later = (fn: () => void, ms: number) => {
      timer = window.setTimeout(() => !stop && fn(), ms);
    };
    const step = () => {
      const tiles = [...(grid.current?.querySelectorAll<HTMLElement>("[data-before]") ?? [])].filter((el) => {
        const r = el.getBoundingClientRect();
        return el.dataset.slug !== last && r.bottom > 0 && r.top < innerHeight;
      });
      if (!tiles.length || document.hidden) return later(step, 3000);
      const el = tiles[Math.floor(Math.random() * tiles.length)];
      const slug = el.dataset.slug!;
      last = slug;
      const pre = new Image();
      pre.src = el.dataset.before!;
      const go = () => {
        if (stop) return;
        setPrimed((s) => (s.has(slug) ? s : new Set(s).add(slug)));
        later(() => {
          setFading(slug);
          later(() => {
            setFading(null);
            later(step, 1800 + Math.random() * 1200);
          }, 2600);
        }, 60);
      };
      pre.decode().then(go, go);
    };
    later(step, 2500);
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [open, filter]);

  function pick(e: MouseEvent, f: Filter) {
    if (plain(e)) return;
    e.preventDefault();
    if (f === filter) return;
    smoothly(() => {
      setFilter(f);
      setPages(1);
      setFading(null);
    });
    window.history.pushState(null, "", filterPath(f));
  }


  function openTile(e: MouseEvent<HTMLElement>, slug: string) {
    if (plain(e)) return;
    e.preventDefault();
    opener.current = e.currentTarget;
    back.current = location.pathname + location.search;
    window.history.pushState(null, "", `/polsterei/${slug}/`);
    pushed.current = true;
    setOpen(slug);
  }

  // while a popup is open, load the photos of the works before and after it, so a switch shows them at once
  useEffect(() => {
    if (!open) return;
    const i = list.findIndex((p) => p.slug === open);
    for (const n of [list[(i + 1) % list.length], list[(i - 1 + list.length) % list.length]])
      for (const id of [n.before, n.after]) if (id) new Image().src = d(id);
  }, [open, list]);

  function step(dir: number) {
    const i = list.findIndex((p) => p.slug === open);
    const next = list[(i + dir + list.length) % list.length];
    window.history.replaceState(null, "", `/polsterei/${next.slug}/`);
    setOpen(next.slug);
  }

  function close() {
    setOpen(null);
    if (pushed.current) {
      pushed.current = false;
      window.history.back();
    } else {
      window.history.replaceState(null, "", back.current);
    }
    opener.current?.focus();
  }

  const current = open ? projectBySlug(open) : undefined;
  // the tab title follows the filter and the open popup (the pre-rendered pages carry the same titles)
  useEffect(() => {
    document.title = (current ? current.title : filterTitle(filter)) + SUFFIX;
  }, [current, filter]);


  // Sofas: headline column 60 %, so its long heading takes two lines; everything else 1/3 headline, 2/3 text
  const wide = filter === "sofas";
  const stack = wide ? (["sofas"] as Filter[]) : filters.filter((f) => f !== "sofas");

  return (
    <>
      <section className="wrap flex flex-col gap-6 pt-10 pb-3 min-[640px]:gap-9 min-[961px]:pt-14">
        <Breadcrumbs
          trail={filter === "alle" ? [{ label: "Polsterei" }] : [{ href: "/polsterei/", label: "Polsterei" }, { label: filterLabel(filter) }]}
        />
        {/* headline left, short intro right, both starting at the top; stacked below 900 px. Sized so the longest headline ("Sofa – modern oder Rokoko, Biedermeier & Co.") takes two lines */}
        <div
          className={`grid items-start gap-x-14 gap-y-6 min-[640px]:gap-y-9 min-[900px]:pb-3 ${
            wide ? "min-[900px]:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]" : "min-[900px]:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
          }`}
        >
          {/* From 900 px up, the headlines and intros of all filters with the same layout sit stacked in one grid cell, only the
              current one visible: the block is as tall as the longest, so the filter row below
              doesn't jump on a switch (stacked below 900 px it moves anyway). Sofas has its own wider headline column. */}
          <PageTitle tilt="rotate-1" stack size="text-[clamp(24px,2.8vw,40px)]">
            {stack.map((f) => (
              <span
                key={f}
                className={`[grid-area:1/1] ${f === filter ? "" : "invisible max-[899px]:hidden"}`}
                aria-hidden={f !== filter}
              >
                {filterHeadline(f)}
              </span>
            ))}
          </PageTitle>
          <div className="grid max-w-[72ch] text-ink-2 min-[900px]:pt-1.5">
            {stack.map((f) => (
              <p key={f} className={`m-0 [grid-area:1/1] ${f === filter ? "" : "invisible max-[899px]:hidden"}`} aria-hidden={f !== filter}>
                {introText(f)}
              </p>
            ))}
          </div>
        </div>
      </section>


      {/* sticks under the (compact) header while scrolling; one scrollable row on phones */}
      <div data-under-header className="sticky top-[var(--header-h,58px)] z-20 bg-bg" style={{ viewTransitionName: "gallery-filters" }}>
        <nav
          aria-label="Arbeiten filtern"
          className="wrap flex gap-2 overflow-x-auto pt-3 pb-4 [scrollbar-width:none] min-[761px]:flex-wrap"
        >
          {filters.map((f) => {
            const on = f === filter;
            return (
              <a
                key={f}
                href={filterPath(f)}
                onClick={(e) => pick(e, f)}
                aria-current={on ? "page" : undefined}
                className={`flt flex flex-none items-center gap-2 rounded-full border-[1.5px] px-[18px] py-2.5 text-[15px] font-semibold no-underline ${
                  on ? "border-btn bg-btn text-btn-ink hover:text-btn-ink" : "border-line-strong text-ink hover:border-ink hover:text-ink"
                }`}
              >
                <span className={`sw ${f === "alle" ? "sw-all" : ""}`} style={{ backgroundColor: filterSwatch(f) }} />
                {filterLabel(f)} <span className="font-medium opacity-70">{inFilter(f).length}</span>
              </a>
            );
          })}
        </nav>
      </div>

      <section className="wrap flex flex-col gap-10 pt-3 pb-24">
        <div ref={grid} className="mgrid">
          {visible.map((p, i) => {
            const s = size(p.after);
            const on = fading === p.slug;
            return (
              <a
                key={p.slug}
                href={`/polsterei/${p.slug}/`}
                onClick={(e) => openTile(e, p.slug)}
                aria-haspopup="dialog"
                data-slug={p.slug}
                data-before={p.before ? d(p.before) : undefined}
                className={`tile mtile block text-ink no-underline hover:text-ink ${i >= per ? "tile-in" : ""}`}
                style={{
                  ...(places.tiles[i] as CSSProperties),
                  viewTransitionName: `tile-${p.slug}`,
                  // a loaded batch rises in one after another
                  animationDelay: i >= per ? `${(i % per) * 45}ms` : undefined,
                }}
              >
                <div className="tile-img relative w-full overflow-hidden rounded-[6px] bg-muted" style={{ aspectRatio: p.ratio }}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={d(p.after)}
                    alt={`${p.title}, fertig gepolstert`}
                    width={s.width}
                    height={s.height}
                    loading={i < 6 ? "eager" : "lazy"}
                    decoding="async"
                    className="block h-full w-full object-cover"
                  />
                  {p.before && primed.has(p.slug) && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={d(p.before)}
                      alt=""
                      aria-hidden="true"
                      className={`tile-before absolute inset-0 block h-full w-full object-cover ${on ? "opacity-100" : "opacity-0"}`}
                    />
                  )}
                  <div className="absolute right-2.5 bottom-3.5 left-3.5 flex items-end gap-2">
                    <span aria-hidden="true" className="contents">
                      <Tape className={tapeSize(p.title)}>{p.title}</Tape>
                    </span>
                    {p.badge && (
                      <span className="ml-auto rounded-full bg-accent-fill px-2.5 py-[3px] text-xs font-semibold text-white">{p.badge}</span>
                    )}
                  </div>
                </div>
              </a>
            );
          })}
          {shown > 0 && <div aria-hidden="true" className="mspace" style={places.spacer[shown - 1] as CSSProperties} />}
        </div>
        {/* More tiles load as the end of the grid comes near (sentinel, see the effect above). The link stays for
            crawlers and without JavaScript, visually hidden. */}
        <div ref={sentinel} className="flex flex-col items-center gap-3">
          <span className="text-[15px] text-ink-3" aria-live="polite">
            {shown} von {list.length} Arbeiten
          </span>
          {shown < list.length && (
            <a href={`${filterPath(filter)}?seite=${pages + 1}`} className="sr-only">
              {Math.min(per, list.length - shown)} weitere anzeigen
            </a>
          )}
        </div>
      </section>

      {current && <GalleryModal project={current} onClose={close} onPrev={() => step(-1)} onNext={() => step(1)} />}
    </>
  );
}
